/**
 * Per-user home location helpers.
 *
 * A user's `defaultLocation` (set by an admin on the Team Access page) is used
 * to pre-select the location option on any page that offers one — the Tag
 * Library Providers tab, the upload folder picker + returning-patient lookup,
 * and the Boards workflows. The user is always free to change the selection
 * afterwards; the default only fills in the initial value.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

export type HomeLocation = "Iowa" | "Minnesota" | "Colorado";

const LOCATION_VALUES: readonly HomeLocation[] = ["Iowa", "Minnesota", "Colorado"];

function isHomeLocation(v: unknown): v is HomeLocation {
  return typeof v === "string" && (LOCATION_VALUES as readonly string[]).includes(v);
}

/** The signed-in user's home location, or null if none is set. */
export function useDefaultLocation(): HomeLocation | null {
  const { user } = useAuth();
  return isHomeLocation(user?.defaultLocation) ? user.defaultLocation : null;
}

/**
 * One-shot: as soon as the user's default location is known, apply it via
 * `setter` — but only while the current selection is still untouched (null)
 * and only once, so a manual change (or an explicit "All Locations" choice)
 * is never overridden. `T` is each caller's own location union, all of which
 * are supersets of HomeLocation.
 */
export function useApplyDefaultLocation<T extends string>(
  current: T | null,
  setter: (value: T) => void,
): void {
  const def = useDefaultLocation();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) return;
    // Caller already has a value (or the user manually picked one) → stand down.
    if (current !== null) {
      applied.current = true;
      return;
    }
    if (def) {
      setter(def as unknown as T);
      applied.current = true;
    }
  }, [def, current, setter]);
}
